import React, { useState, memo, useCallback, useMemo, useRef, useEffect } from 'react'
import {
  Tr,
  Td,
  HStack,
  VStack,
  Text,
  Box,
  Flex,
  IconButton,
  useColorModeValue,
  Input,
  Checkbox,
  useToast,
  Link,
  Tooltip,
} from '@chakra-ui/react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { FiFile, FiImage, FiTag, FiChevronRight } from 'react-icons/fi'
import { FaStar, FaRegStar } from 'react-icons/fa'
import { FileItem } from '@/types'
import { getFileTypeLabel } from '@/utils/fileSorting'
import { useFavorites } from '@/contexts/FavoritesContext'
import { useNavigation } from '@/hooks/useNavigation'
import { useAnalytics } from '@/hooks/useAnalyticsWithUser'
import { useAppDispatch } from '@/store'
import { renameFile, deleteFile } from '@/store/slices/filesSlice'
import FilePropertiesModal from '@/components/Modals/FilePropertiesModal'
import DeleteFileConfirmationModal from '@/components/Modals/DeleteFileConfirmationModal'
import FilePermissionsModal from '@/components/Modals/FilePermissionsModal'
import { ContextMenu } from '@/components/Common'
import FileProcessingIndicator from '@/components/FileExplorer/FileProcessingIndicator'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

// Add device detection utility
const isTouchDevice = () => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    navigator.msMaxTouchPoints > 0
  )
}

interface FileTableRowProps {
  file: FileItem
  index: number
  showSizeColumn?: boolean
  showTypeColumn?: boolean
  showModifiedColumn?: boolean
  /** Rendered row order, so shift-click ranges follow what is on screen. */
  orderedFileIds?: number[]
  /** True once polling has given up on this page, so the spinner stops promising progress. */
  processingStalled?: boolean
}

const FileTableRow = memo(function FileTableRow({
  file,
  showSizeColumn = true,
  showTypeColumn = true,
  showModifiedColumn = true,
  orderedFileIds,
  processingStalled,
}: FileTableRowProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const toast = useToast()
  const { toggleFavorite, isFavorite } = useFavorites()
  const { selectedFiles, toggleFileSelection, selectFileRange, selectSingleFile, selectedFolder } =
    useNavigation()
  const { trackFileAction } = useAnalytics()
  const {
    t,
    formatFileSize: formatFileSizeI18n,
    formatDateTime,
  } = useAppTranslation(['files', 'common'])

  // Device detection
  const isTouch = useMemo(() => isTouchDevice(), [])

  // Color mode values
  const rowBg = useColorModeValue('transparent', 'transparent')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')
  const selectedBg = useColorModeValue('brand.50', 'brand.900/20')
  const selectedHoverBg = useColorModeValue('brand.75', 'brand.800/30')
  // const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'gray.200')
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400')
  const favoriteHoverBg = useColorModeValue('orange.50', 'orange.900')
  const rowDivider = useColorModeValue('gray.100', 'gray.700')

  // Rename state
  const [isRenaming, setIsRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Context menu state
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })

  // Properties modal state
  const [showPropertiesModal, setShowPropertiesModal] = useState(false)

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Permissions modal state
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  // Memoize expensive calculations
  const isFileFavorite = useMemo(() => isFavorite('file', file.id), [isFavorite, file.id])
  const isSelected = useMemo(() => selectedFiles.has(file.id), [selectedFiles, file.id])
  const [isBeingDragged, setIsBeingDragged] = useState(false)

  const handleToggleFavorite = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      try {
        await toggleFavorite('file', file)
      } catch (error) {
        console.error('Failed to toggle favorite:', error)
      }
    },
    [toggleFavorite, file]
  )

  const handleDownload = async () => {
    try {
      const downloadUrl = await apiService.generateSecureFileUrl(file.id, 'download')
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Failed to generate download URL:', error)
      // Fallback to legacy method
      const fallbackUrl = apiService.getFileDownloadUrl(file.id)
      window.open(fallbackUrl, '_blank')
    }
  }

  // Use i18n formatFileSize function instead of local implementation

  // Rename functionality
  const handleRename = useCallback((file: FileItem) => {
    setNewName(file.display_name || file.original_name)
    setIsRenaming(true)
  }, [])

  const handleRenameSubmit = useCallback(async () => {
    if (newName.trim() && newName.trim() !== (file.display_name || file.original_name)) {
      try {
        await dispatch(renameFile({ fileId: file.id, displayName: newName.trim() }))
      } catch (error) {
        console.error('Failed to rename file:', error)
      }
    }
    setIsRenaming(false)
    setNewName('')
  }, [dispatch, file.id, file.display_name, file.original_name, newName])

  const handleRenameCancel = useCallback(() => {
    setIsRenaming(false)
    setNewName('')
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleRenameSubmit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleRenameCancel()
      }
    },
    [handleRenameSubmit, handleRenameCancel]
  )

  // Auto-focus input when renaming starts
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isRenaming])

  // Context menu handlers
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (isRenaming) {
      return
    }

    setContextMenuPosition({
      x: event.clientX,
      y: event.clientY,
    })
    setShowContextMenu(true)
  }, [])

  const handleCloseContextMenu = useCallback(() => {
    setShowContextMenu(false)
  }, [])

  const handleContextMenuRename = useCallback(() => {
    handleRename(file)
    handleCloseContextMenu()
  }, [file])

  const handleContextMenuDownload = useCallback(async () => {
    await handleDownload()
    handleCloseContextMenu()
  }, [handleDownload])

  const handleContextMenuProperties = useCallback(() => {
    setShowPropertiesModal(true)
    handleCloseContextMenu()
  }, [])

  const handleContextMenuDelete = useCallback(() => {
    setShowDeleteModal(true)
    handleCloseContextMenu()
  }, [])

  const handleContextMenuShare = useCallback(() => {
    setShowPermissionsModal(true)
    handleCloseContextMenu()
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!file) return

    setIsDeleting(true)

    try {
      await dispatch(
        deleteFile({
          fileId: file.id,
          folderId: selectedFolder?.id || null,
        })
      ).unwrap()

      toast({
        title: t('fileActions.deleteSuccess', { ns: 'files' }),
        description: t('fileActions.deleteSuccessDescription', {
          ns: 'files',
          fileName: file.display_name || file.original_name,
        }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      setShowDeleteModal(false)
    } catch (error) {
      console.error('Failed to delete file:', error)
      toast({
        title: t('fileActions.deleteError', { ns: 'files' }),
        description: t('fileActions.deleteErrorDescription', { ns: 'files' }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsDeleting(false)
    }
  }, [dispatch, file, selectedFolder?.id, toast, t])

  // Use i18n formatDateTime function instead of local implementation

  const getFileIcon = (mimeType: string) => {
    // Check for images first
    if (mimeType?.startsWith('image/')) {
      return <FiImage color="#805ad5" />
    }
    // Keep existing PDF logic
    if (mimeType === 'application/pdf') {
      return <FiFile color="#e53e3e" />
    }
    // Default icon
    return <FiFile />
  }

  const handleRowSelection = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isRenaming) {
      return
    }

    // On touch devices, single tap opens the file
    if (isTouch) {
      // Track file preview action
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      trackFileAction('preview', fileExtension, {
        file_id: file.id,
        file_size: file.file_size,
        folder_id: selectedFolder?.id,
        interaction_type: 'touch',
      })
      navigate(`/preview/${file.id}`)
    } else {
      // On desktop, handle different click modes based on modifier keys
      if (e.shiftKey) {
        // Shift+click for range selection
        e.preventDefault()
        selectFileRange(file.id, orderedFileIds)
      } else if (e.metaKey || e.ctrlKey) {
        // Cmd+click (macOS) or Ctrl+click (Windows) for individual toggle
        e.preventDefault()
        toggleFileSelection(file.id)
      } else {
        // Regular click - select only this file (clear others)
        e.preventDefault()
        selectSingleFile(file.id)
      }
    }
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isRenaming) {
      return
    }

    // Only handle double-click on desktop devices
    if (!isTouch) {
      // Track file preview action
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      trackFileAction('preview', fileExtension, {
        file_id: file.id,
        file_size: file.file_size,
        folder_id: selectedFolder?.id,
        interaction_type: 'double_click',
      })
      navigate(`/preview/${file.id}`)
    }
  }

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent) => {
    if (isRenaming) {
      return
    }

    const selectedFilesList = Array.from(selectedFiles)

    // Create custom drag image
    const dragImage = document.createElement('div')
    dragImage.style.position = 'absolute'
    dragImage.style.top = '-1000px'
    dragImage.style.left = '-1000px'
    dragImage.style.padding = '8px 12px'
    dragImage.style.backgroundColor = '#3182ce'
    dragImage.style.color = 'white'
    dragImage.style.borderRadius = '8px'
    dragImage.style.fontSize = '14px'
    dragImage.style.fontWeight = '600'
    dragImage.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
    dragImage.style.zIndex = '1000'

    // If current file is not selected, select it and drag just this file
    if (!isSelected) {
      toggleFileSelection(file.id)
      dragImage.textContent = `📄 ${t('fileActions.movingFile', { ns: 'files' })}`
      const dragData = {
        type: 'files',
        fileIds: [file.id],
        fileNames: [file.original_name],
      }
      e.dataTransfer.setData('application/json', JSON.stringify(dragData))
    } else {
      // Drag all selected files
      const fileCount = selectedFilesList.length
      dragImage.textContent = `📄 ${t('fileActions.movingFiles', { ns: 'files', count: fileCount })}`

      const selectedFileNames = selectedFilesList.map(() => {
        // This is a simplified lookup - in real app you'd want to optimize this
        return file.original_name // For now just use current file name
      })

      const dragData = {
        type: 'files',
        fileIds: selectedFilesList,
        fileNames: selectedFileNames,
      }
      e.dataTransfer.setData('application/json', JSON.stringify(dragData))
    }

    // Add drag image to DOM temporarily
    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 60, 20)

    // Clean up drag image after drag starts
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage)
      }
    }, 0)

    e.dataTransfer.effectAllowed = 'move'
    setIsBeingDragged(true)
  }

  const handleDragEnd = () => {
    if (isRenaming) {
      return
    }
    setIsBeingDragged(false)
  }

  return (
    <>
      {/* Hover is a flat background change only — lifting the row shifted
          every neighbour at this row density. */}
      <Tr
        _hover={{ bg: isSelected ? selectedHoverBg : hoverBg }}
        bg={isSelected ? selectedBg : rowBg}
        opacity={isBeingDragged ? 0.5 : 1}
        cursor={isRenaming ? 'default' : 'pointer'}
        draggable={!isRenaming}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={handleRowSelection}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        transition="background-color 0.15s ease-in-out"
        borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
        borderLeftColor={isSelected ? 'brand.500' : 'transparent'}
      >
        {/* Name */}
        {/* Selection — stops propagation so it doesn't also fire the row's
            click-to-select / double-click-to-open handlers. */}
        <Td
          borderBottom="1px solid"
          borderColor={rowDivider}
          py={2}
          pl={4}
          pr={0}
          w="36px"
          minW="36px"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            isChecked={isSelected}
            onChange={() => toggleFileSelection(file.id)}
            aria-label={t('buttons.select', { ns: 'common' })}
          />
        </Td>

        <Td borderBottom="1px solid" borderColor={rowDivider} py={2} px={3} w="100%">
          <HStack spacing={3} align="center" w="100%">
            <Box>{getFileIcon(file.mime_type)}</Box>

            {/* File name row */}
            <HStack spacing={1} align="center" flex={1} minW={0}>
              {isRenaming ? (
                <Input
                  ref={inputRef}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleRenameCancel}
                  size="sm"
                  fontSize="sm"
                  fontWeight="600"
                  variant="outline"
                  flex={1}
                  autoFocus
                />
              ) : (
                <>
                  <Link
                    as={RouterLink}
                    to={`/preview/${file.id}`}
                    fontWeight="500"
                    fontSize="sm"
                    cursor="pointer"
                    color={textColor}
                    _hover={{ color: 'brand.600', textDecoration: 'none' }}
                    transition="colors 0.15s"
                    noOfLines={1}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey) {
                        e.stopPropagation()
                        return
                      }
                      e.preventDefault()
                    }}
                  >
                    {file.display_name || file.original_name}
                  </Link>
                  {file.tags && file.tags.length > 0 && (
                    <Tooltip
                      label={
                        <VStack spacing={1} align="start" p={1}>
                          {file.tags.map((tag) => (
                            <Text key={tag.id} fontSize="xs">
                              {tag.tag}
                            </Text>
                          ))}
                        </VStack>
                      }
                      placement="top"
                      hasArrow
                    >
                      <Box
                        as="span"
                        color={mutedTextColor}
                        _hover={{ color: 'brand.500' }}
                        cursor="default"
                        display="flex"
                        alignItems="center"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <FiTag size={12} />
                      </Box>
                    </Tooltip>
                  )}
                  <FileProcessingIndicator file={file} isStalled={processingStalled} />
                </>
              )}
            </HStack>
          </HStack>
        </Td>

        {/* Favourite */}
        <Td borderBottom="1px solid" borderColor={rowDivider} py={2} px={2} w="48px" minW="48px">
          <Flex justify="center">
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={t('fileActions.toggleFavorite', { ns: 'files' })}
              icon={isFileFavorite ? <FaStar color="#dd6b20" size={14} /> : <FaRegStar size={14} />}
              color={isFileFavorite ? undefined : 'gray.300'}
              onClick={handleToggleFavorite}
              _hover={{ bg: favoriteHoverBg }}
              borderRadius="full"
              transition="all 0.15s"
            />
          </Flex>
        </Td>

        {/* Size */}
        {showSizeColumn && (
          <Td
            borderBottom="1px solid"
            borderColor={rowDivider}
            py={2}
            px={3}
            w="104px"
            minW="104px"
            textAlign="right"
          >
            <Text fontSize="sm" color={mutedTextColor} fontWeight="500" whiteSpace="nowrap">
              {formatFileSizeI18n(file.file_size)}
            </Text>
          </Td>
        )}

        {/* Type */}
        {showTypeColumn && (
          <Td
            borderBottom="1px solid"
            borderColor={rowDivider}
            py={2}
            px={3}
            w="88px"
            minW="88px"
            textAlign="right"
          >
            <Text fontSize="sm" color={mutedTextColor} fontWeight="500" whiteSpace="nowrap">
              {getFileTypeLabel(file)}
            </Text>
          </Td>
        )}

        {/* Modified */}
        {showModifiedColumn && (
          <Td
            borderBottom="1px solid"
            borderColor={rowDivider}
            py={2}
            px={3}
            w="170px"
            minW="170px"
            textAlign="right"
          >
            <Text fontSize="sm" color={mutedTextColor} fontWeight="500" whiteSpace="nowrap">
              {formatDateTime(new Date(file.updated_at))}
            </Text>
          </Td>
        )}

        {/* Open affordance */}
        <Td borderBottom="1px solid" borderColor={rowDivider} py={2} px={3} w="36px" minW="36px">
          <Box color="gray.300" display="flex" alignItems="center" justifyContent="center">
            <FiChevronRight size={16} />
          </Box>
        </Td>
      </Tr>

      {/* Context Menu */}
      <ContextMenu
        isOpen={showContextMenu}
        position={contextMenuPosition}
        onClose={handleCloseContextMenu}
        items={[
          {
            label: t('buttons.rename', { ns: 'common' }),
            onClick: handleContextMenuRename,
          },
          {
            label: t('buttons.download', { ns: 'common' }),
            onClick: handleContextMenuDownload,
          },
          {
            label: t('buttons.properties', { ns: 'common' }),
            onClick: handleContextMenuProperties,
          },
          {
            label: t('fileActions.share', { ns: 'files' }),
            onClick: handleContextMenuShare,
          },
          {
            label: t('buttons.delete', { ns: 'common' }),
            onClick: handleContextMenuDelete,
            isDestructive: true,
          },
        ]}
      />

      {/* File Properties Modal */}
      <FilePropertiesModal
        isOpen={showPropertiesModal}
        onClose={() => setShowPropertiesModal(false)}
        file={file}
      />

      {/* Delete Confirmation Modal */}
      <DeleteFileConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        file={file}
        isLoading={isDeleting}
      />

      {/* File Permissions Modal */}
      <FilePermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        fileId={file.id}
        fileName={file.display_name || file.original_name}
      />
    </>
  )
})

export default FileTableRow
